import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import styles from './SupportTicketModals.module.css';
import iconClose from '../../../images/icon-close.svg';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { createSupportTicket, getSupportTickets } from '../../../redux/support-ticket/actions/support-ticket.action';
import Loader from '../../common/loader/Loader';
import Select from 'react-select';
import { colourStyles, locationsData } from '../../locations/Locations';
import { getLocation } from '../../../redux/location/actions/location.action';
import { trimData } from '../../../utils/trim-data';

interface RaiseSupportTicketFormData {
    locationId: string;
    subject: string;
    description: string;
}

interface RaiseSupportTicketProps {
    onClose: () => void;
    setShowToast: (show: boolean) => void;
    setToastMessage: (message: string) => void;
    setToastType: (type: 'success' | 'error') => void;
}

export const RaiseSupportTicket = ({ onClose, setShowToast, setToastMessage, setToastType }: RaiseSupportTicketProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState(false);
    const allLocationsList = useSelector((state: IRootState) => state.locationReducer.data.locations);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const [locations, setLocations] = useState<locationsData[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<{ value: string; label: string }>({
        value: '',
        label: 'All Locations',
    });
    const { control, handleSubmit, reset, formState: { errors } } = useForm<RaiseSupportTicketFormData>({
        defaultValues: {
            subject: '',
            description: ''
        }
    });
    useEffect(() => {
        dispatch(getLocation(undefined, undefined, true));
    }, []);

    useEffect(() => {
        if (!invitedLocations?.length && !allLocationsList?.length) {
            setLocations([]);
        } else {
            if (invitedLocations?.length || allLocationsList?.length) {
                const data = invitedLocations?.map((list: locationsData) => ({
                    ...list,
                    isRestricted: true,
                }));
                setLocations([...(data ?? []), ...(allLocationsList ?? [])]);
            }
        }
    }, [allLocationsList?.length, invitedLocations?.length]);

    const locationOptions = useMemo(
        () =>
            locations.map((loc: locationsData) => ({
                value: loc.id ?? '',
                label: loc.name ?? '',
            })),
        [locations]
    );

    const handleTrimStartChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        onChange: (value: string) => void
    ) => {
        const trimmedValue = e.target.value.trimStart();
        onChange(trimmedValue);
    };

    const onSubmit: SubmitHandler<RaiseSupportTicketFormData> = (data) => {
        const trim = (val: string) => trimData<string>(val) || '';
        const payload: { subject: string; description: string; locationId?: string } = {
            subject: trim(data.subject) || '',
            description: trim(data.description) || '',
        };
        if (selectedLocation.value) {
            payload.locationId = selectedLocation.value;
        }
        dispatch(
            createSupportTicket(
                payload,
                setLoading,
                (message) => {
                    setShowToast(true);
                    setToastMessage(message);
                    setToastType('success');
                    dispatch(getSupportTickets({ searchText: '', page: 1, limit: 10 }));
                    reset();
                    onClose();
                },
                (error) => {
                    setShowToast(true);
                    setToastMessage(error);
                    setToastType('error');
                }
            )
        );
    };

    return (
        <div className={styles.modalContent}>
            {loading && <Loader />}
            <div className={styles.modalHeader}>
                <h2>Raise Support Ticket</h2>
                <div className={styles.closeIcon} onClick={onClose}>
                    <img src={iconClose} alt="Close" />
                </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.modalBody}>
                    <div className={`${styles.formGroup} ${styles.lockerWrapper}`}>
                        <label>
                            Property Name
                        </label>
                        <Controller
                            name="locationId"
                            control={control}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <Select
                                        onChange={(selectedOption) => {
                                            if (selectedOption?.value) {
                                                setSelectedLocation(selectedOption);
                                            }
                                        }}
                                        isSearchable={true}
                                        classNamePrefix="custom-react-select"
                                        value={
                                            selectedLocation.value !== ''
                                                ? { value: selectedLocation.value, label: selectedLocation.label }
                                                : { value: '', label: 'Select Property' }
                                        }
                                        options={locationOptions}
                                        placeholder="Select Location"
                                        styles={colourStyles}
                                    />
                                );
                            }}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="subject">Subject</label>
                        <Controller
                            name="subject"
                            control={control}
                            rules={{ required: 'Subject is required' }}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="subject"
                                    type="text"
                                    placeholder="Brief description of your issue"
                                    onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                    className={`${styles.inputField} ${errors.subject ? styles.inputError : ''}`}
                                />
                            )}
                        />
                        {errors.subject && <span className={styles.errorMessage}>{errors.subject.message}</span>}
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="description">Description</label>
                        <Controller
                            name="description"
                            control={control}
                            rules={{ required: 'Description is required' }}
                            render={({ field }) => (
                                <textarea
                                    {...field}
                                    id="description"
                                    placeholder="Please provide detailed information about your issue."

                                    onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                    className={`${styles.textareaField} ${errors.description ? styles.inputError : ''}`}
                                />
                            )}
                        />
                        {errors.description && <span className={styles.errorMessage}>{errors.description.message}</span>}
                    </div>
                </div>
                <div className={styles.modalFooter}>
                    <button type="button" className={styles.cancelBtn} onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className={styles.submitBtn} disabled={loading}>
                        {loading ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </form>
        </div>
    );
};
